/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import authService from '../api/authService';

const readUser = () => {
  try {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// Backend ne vraća token uvek na istom mestu — pokrij sve poznate varijante.
const extractToken = (res) => {
  const authHeader = res.headers?.authorization || res.headers?.Authorization || '';
  return (
    res.data?.token ||
    res.data?.accessToken ||
    res.data?.access_token ||
    res.data?.jwt ||
    res.data?.data?.token ||
    (authHeader.startsWith('Bearer ') ? authHeader.slice(7) : authHeader) ||
    ''
  );
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readUser);

  useEffect(() => {
    const refresh = () => setUser(readUser());
    window.addEventListener('auth-changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('auth-changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authService.login(email, password);
    const token = extractToken(res);
    if (token) {
      localStorage.setItem('token', token);
    } else {
      console.warn('Login uspešan ali token nije pronađen u response-u');
    }
    const userData = res.data?.user || res.data?.korisnik || res.data?.data?.user || { email };
    localStorage.setItem('user', JSON.stringify(userData));
    window.dispatchEvent(new Event('auth-changed'));
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('auth-changed'));
  }, []);

  // Upis svežih podataka o korisniku (npr. posle izmene profila).
  const updateUser = useCallback((updated) => {
    localStorage.setItem('user', JSON.stringify(updated));
    window.dispatchEvent(new Event('auth-changed'));
  }, []);

  const value = useMemo(
    () => ({ user, isLoggedIn: !!user, login, logout, updateUser }),
    [user, login, logout, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
